import clsx from 'clsx';
import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { matchSorter } from 'match-sorter';
import useTranslation from 'next-translate/useTranslation';
import dynamic from 'next/dynamic';

//types
import type { OptionType } from '@/components/base/Select';
import type { StatusType } from '@/lib/socialprofiles/status';
import type { SocialProfileType } from '@/types/socialProfileType';

// Components
import Icon from '@/components/base/Icon';
import Input from '@/components/base/Input';
import Select from '@/components/base/Select';
import SocialProfile from '@/components/pages/socialprofiles/SocialProfile';
import { ActionsBar } from '@/components/pages/socialprofiles/group/ActionsBar';
const SaveSelectedProfilesAsGroupModal = dynamic(() =>
    import('@/components/composite/SpGroups/SaveSelectedProfilesAsGroupModal').then(
        (mod) => mod.SaveSelectedProfilesAsGroupModal
    )
);
const DeleteSocialProfileModal = dynamic(
    () => import('@/components/pages/socialprofiles/DeleteSocialProfileModal')
);

// Hooks
import useSocialProfiles from '@/hooks/swr/useSocialProfiles';
import useToggle from '@/utils/hooks/utils/useToggle';
import useUser from '@/utils/hooks/swr/useUser';
import useIntersectionObserver from '@/utils/hooks/utils/useIntersectionObserver';
import useSPGroups from '@/utils/hooks/swr/group/useSPGroups';
import useActiveGroup from '@/utils/hooks/user/useActiveGroup';
import useActiveTreeStructure from '@/utils/hooks/tree-structure/useActiveTreeStructure';
import { useManageProfilesAdded } from '@/utils/hooks/socialprofiles/useManageProfilesAdded';

// Lib
import { makeSortOptions, makeDisplayedStatus } from '@/lib/socialprofiles/status';

const MAX_DISPLAYED_SPS_LIMIT = 30;

const SortButton = ({ selected }: { selected: SocialProfilesSelectOptionType }) => {
    const { t } = useTranslation('socialprofiles');
    const { isTreeStructureActivated } = useActiveTreeStructure();
    return (
        <Select.Button
            baseStyle={isTreeStructureActivated ? 'whiteWithBorder' : 'noBorder'}
            selected={selected}>
            <span className="flex items-center">
                <Icon
                    icon={selected.button.icon}
                    className={clsx('mr-2', selected.button.iconColor)}
                />
                {t('sort_by') + ' ' + selected.button.text}
            </span>
        </Select.Button>
    );
};

type SocialProfilesSelectOptionType = OptionType & {
    button: {
        text: string;
        icon: string;
        iconColor: string;
    };
    order: StatusType[];
};

export type DisplayedSocialProfileType = SocialProfileType & {
    displayedStatus?: ReturnType<typeof makeDisplayedStatus>;
};

const SocialProfilesList = ({ isOnTrial, addSocialProfile }) => {
    const { t } = useTranslation('socialprofiles');
    const { isTreeStructureActivated } = useActiveTreeStructure();
    const [search, setSearch] = useState('');
    const [isQuickAddGroup, setIsQuickAddGroup] = useState<boolean>(false);
    const {
        isOpen,
        toggle,
        params: selectedSocialProfiles,
        setParams: setSelectedSocialProfiles
    } = useToggle<DisplayedSocialProfileType[]>();
    const { isOpen: isDeleteModalOpen, toggle: toggleDeleteModal } = useToggle();

    const {
        socialProfiles,
        errorAuthOtherSocialProfiles,
        errorAuthDataAccessSocialProfiles,
        errorAuthOtherSchedulerPermissions,
        errorAuthSchedulerPermissions,
        errorAuthTeamSchedulerPermissions,
        errorAuthTeamOtherSchedulerPermissions,
        mutateSocialProfiles
    } = useSocialProfiles({
        shouldRetryOnError: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const {
        updateGroupDataAndStorage,
        removeActiveSocialProfileFromGroup,
        removeActiveGroupIfNoProfile,
        activeGroup
    } = useActiveGroup();

    const { manageProfilesAdded } = useManageProfilesAdded();

    const { spGroups, mutateSpGroups } = useSPGroups({
        revalidateIfStale: false
    });

    const { mutateUser } = useUser();

    const sortOptions = makeSortOptions(t);
    const hasDisconnectedProfiles =
        errorAuthDataAccessSocialProfiles?.length ||
        errorAuthOtherSocialProfiles?.length ||
        errorAuthSchedulerPermissions?.length ||
        errorAuthOtherSchedulerPermissions?.length ||
        errorAuthTeamSchedulerPermissions?.length ||
        errorAuthTeamOtherSchedulerPermissions?.length;

    const [sortBy, setSortBy] = useState<SocialProfilesSelectOptionType>(
        sortOptions.filter((option) => {
            if (hasDisconnectedProfiles) {
                return option?.value === 'disconnected';
            }
            return option.value === 'connected';
        })?.[0]
    );

    const displayedSocialProfile: DisplayedSocialProfileType[] = useMemo(() => {
        if (socialProfiles) {
            const filteredSocialProfiles = matchSorter(socialProfiles, search, {
                keys: ['slug', 'external_api_data.name', 'groups.*.name']
            });
            return filteredSocialProfiles
                .map((profile) => {
                    const displayedStatus = makeDisplayedStatus(profile);
                    return { ...profile, displayedStatus };
                })
                .sort((a, b) => {
                    const orderA = sortBy?.order?.indexOf(a.displayedStatus);
                    const orderB = sortBy?.order?.indexOf(b.displayedStatus);
                    return orderA - orderB;
                });
        }
        return [];
    }, [search, socialProfiles, sortBy?.order]);

    const numberActiveProfiles = socialProfiles?.reduce(
        (activeNumber, profile) => (profile.active ? activeNumber + 1 : activeNumber),
        0
    );

    const refTest = useRef<HTMLDivElement>();
    const isInViewport = useIntersectionObserver(refTest);
    const [maxDisplayedSPs, setMaxDisplayedSPs] = useState(MAX_DISPLAYED_SPS_LIMIT);

    useEffect(() => {
        if (isInViewport && displayedSocialProfile?.length > maxDisplayedSPs) {
            setMaxDisplayedSPs((prevState) => prevState + MAX_DISPLAYED_SPS_LIMIT);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isInViewport]);

    function handleSelectSocialProfile(socialProfile: DisplayedSocialProfileType) {
        const isSpSelected = selectedSocialProfiles?.find((sp) => sp.id === socialProfile.id);
        setIsQuickAddGroup(false);
        if (isSpSelected) {
            const newSocialProfilesSelected = selectedSocialProfiles.filter(
                (sp) => sp.id !== socialProfile.id
            );
            return setSelectedSocialProfiles(newSocialProfilesSelected);
        }
        return setSelectedSocialProfiles((prevState) => [...(prevState ?? []), socialProfile]);
    }

    const socialProfilesToSend = useCallback(() => {
        const socialProfilesIds = selectedSocialProfiles?.map((profile) => profile.id) ?? [];
        const filteredSocialProfilesIds = displayedSocialProfile.map((profile) => profile.id);
        const filteredSocialProfiles = socialProfilesIds.filter((id) =>
            filteredSocialProfilesIds.includes(id)
        );
        return filteredSocialProfiles;
    }, [selectedSocialProfiles, displayedSocialProfile]);

    async function handleDeleteSuccess() {
        setSelectedSocialProfiles([]);
        await mutateUser();
        return await mutateSocialProfiles();
    }

    const updateSocialProfile = async (socialProfile: DisplayedSocialProfileType) => {
        await mutateSocialProfiles();
        await mutateUser();
        if (activeGroup?.social_profiles.find((sp) => sp.id === socialProfile.id)) {
            const newSocialProfilesList = activeGroup.social_profiles.map((sp) => {
                if (sp.id === socialProfile.id) {
                    return {
                        ...sp,
                        active: !socialProfile.active
                    };
                }
                return sp;
            });
            updateGroupDataAndStorage({
                ...activeGroup,
                social_profiles: newSocialProfilesList
            });
            removeActiveSocialProfileFromGroup(newSocialProfilesList);
            if (
                !newSocialProfilesList.filter((socialProfile) => socialProfile.active).length &&
                socialProfile.active
            ) {
                removeActiveGroupIfNoProfile();
            }
        }
        if (spGroups?.length) {
            await mutateSpGroups();
        }
    };

    const onSearch = (value: string) => {
        if (maxDisplayedSPs > MAX_DISPLAYED_SPS_LIMIT) {
            setMaxDisplayedSPs(MAX_DISPLAYED_SPS_LIMIT);
        }
        setSearch(value);
    };

    return (
        <div
            className={clsx('relative flex flex-col space-y-10 lg:min-w-[66%] lg:flex-1', {
                'space-y-10': !isTreeStructureActivated,
                'space-y-5': isTreeStructureActivated
            })}>
            <div
                className={clsx('flex w-full flex-col justify-between gap-3', {
                    'sm:flex-row': !isTreeStructureActivated,
                    '@xl/wrapper-platform-content:flex-row': isTreeStructureActivated
                })}>
                <div className="flex-1">
                    <Input
                        data-testid="search-input"
                        className="border-0"
                        id="search-social-profiles"
                        placeholder={t('search_social_profiles')}
                        value={search}
                        onChange={(e) => onSearch(e.target.value)}
                        beforeInput={<Icon icon="search" />}
                        noBorderShadow={!isTreeStructureActivated}
                        inputStyle={isTreeStructureActivated ? 'default_no_background' : 'default'}
                    />
                </div>
                <div>
                    <Select
                        className="border-0"
                        selected={sortOptions.find((s) => s.value === sortBy.value)}
                        setSelected={(option: SocialProfilesSelectOptionType) => setSortBy(option)}
                        options={sortOptions}
                        button={<SortButton selected={sortBy} />}
                    />
                </div>
            </div>
            <div className="flex flex-col space-y-3">
                {!isQuickAddGroup && selectedSocialProfiles?.length ? (
                    <ActionsBar
                        toggleGroupModal={toggle}
                        unSelectAll={() => setSelectedSocialProfiles([])}
                        nbSelectedSocialProfiles={socialProfilesToSend().length}
                        toggleDeleteMultiple={toggleDeleteModal}
                        canDelete={numberActiveProfiles > 1}
                    />
                ) : null}
                <div className="flex flex-1 flex-col gap-3">
                    {displayedSocialProfile?.length ? (
                        displayedSocialProfile?.slice(0, maxDisplayedSPs).map((sp) => (
                            <SocialProfile
                                key={sp.id}
                                socialProfile={sp}
                                isOnTrial={isOnTrial}
                                addSocialProfile={addSocialProfile}
                                cantDelete={numberActiveProfiles <= 1}
                                mutateSocialProfiles={mutateSocialProfiles}
                                handleSelectSocialProfile={handleSelectSocialProfile}
                                updateSocialProfile={updateSocialProfile}
                                manageProfilesAdded={manageProfilesAdded}
                                toggleGroupModal={() => {
                                    setSelectedSocialProfiles([sp]);
                                    toggle();
                                    setIsQuickAddGroup(true);
                                }}
                                isSelected={
                                    !isQuickAddGroup &&
                                    Boolean(
                                        selectedSocialProfiles?.find(
                                            (profile) => profile.id === sp.id
                                        )
                                    )
                                }
                            />
                        ))
                    ) : (
                        <div className="flex h-full items-center justify-center">
                            {t('no_social_profiles')}
                        </div>
                    )}
                </div>
            </div>
            <div className="absolute bottom-80 left-0 -z-1 h-px w-full" ref={refTest} />
            <SaveSelectedProfilesAsGroupModal
                isOpen={isOpen}
                toggle={toggle}
                selectedProfiles={selectedSocialProfiles}
            />
            <DeleteSocialProfileModal
                isOpen={isDeleteModalOpen}
                toggle={toggleDeleteModal}
                onSuccess={handleDeleteSuccess}
                socialProfilesIds={socialProfilesToSend()}
            />
        </div>
    );
};

export default SocialProfilesList;
